import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { toast } from "react-toastify";
import SERVICE_BASE_URLS from "./apiConfig";
import { buildMedCloudUploadFormData } from "./medCloudUploadForm";
import { clearSessionAndRedirectToLogin } from "./authSession";
import { mapHealthInterestCategoriesResponse } from "./healthInterestCategories";
import { extractDashboardVideosPayload } from "./dashboardVideos";
import { loginSuccess } from "./authSlice";
import { APP_ENVIRONMENT, APP_VERSION } from "../config/appVersion";
import {
  containsCredentialToken,
  FORGOT_CREDENTIAL_API_PATH,
  FORGOT_CREDENTIAL_MUTATION_KEY,
  RESET_CREDENTIAL_API_PATH,
} from "./credentialUiLabels";

let lastToastMessage = "";
let lastToastAt = 0;

// ─── Refresh mutex ────────────────────────────────────────────────────────────
// Prevents multiple simultaneous refresh calls when many requests 401 at once.
let isRefreshing = false;
let refreshSubscribers = []; // resolvers waiting for the new token

function subscribeTokenRefresh(resolve) {
  refreshSubscribers.push(resolve);
}

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((resolve) => resolve(newToken));
  refreshSubscribers = [];
}

function onTokenRefreshFailed() {
  refreshSubscribers.forEach((resolve) => resolve(null));
  refreshSubscribers = [];
}
// ──────────────────────────────────────────────────────────────────────────────

const PUBLIC_ENDPOINTS = new Set([
  "login",
  "loginWithGoogle",
  "register",
  "sendotp",
  "generateEmailOtp",
  "verifyotp",
  "sendPhoneOtp",
  "verifyPhoneOtp",
  FORGOT_CREDENTIAL_MUTATION_KEY,
]);

const LOCALLY_HANDLED_ERROR_ENDPOINTS = new Set([
  "medCloudUpload",
  "medCloudDeleteFile",
  "medCloudDownloadFile",
  "markAllNotificationsRead",
  "saveUserInterests",
  "acceptTermsAndConditions",
]);

const dynamicBaseQuery = fetchBaseQuery({
  prepareHeaders: (headers, { getState, endpoint }) => {
    headers.set("X-App-Version", APP_VERSION);
    headers.set("X-App-Environment", APP_ENVIRONMENT);

    if (PUBLIC_ENDPOINTS.has(endpoint)) {
      return headers;
    }

    // Always prefer Redux state; fall back to storage.
    let token = getState()?.auth?.token;

    if (!token) {
      token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");
    }

    if (token && token !== "undefined" && token !== "null") {
      let rawToken = token.replaceAll(/(?:^["']|["']$)/g, "");
      if (rawToken.startsWith("Bearer ")) {
        rawToken = rawToken.slice(7);
      }
      headers.set("Authorization", `Bearer ${rawToken}`);
    }

    return headers;
  },
});

function resolveRequestArgs(args) {
  let url = "";
  let service = "employee";
  let method = "GET";
  let body;

  if (typeof args === "string") {
    url = args;
  } else {
    url = args.url;
    service = args.service || service;
    method = args.method || method;
    body = args.body;
  }

  const baseUrl = SERVICE_BASE_URLS[service];

  if (!baseUrl) {
    console.error(`[apiSlice] Missing base URL for service "${service}"`);
    return { url: `/${url}`, method, body };
  }

  return { url: `${baseUrl}${url}`, method, body };
}

function extractErrorMessage(errorObj) {
  const { data, error } = errorObj;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.detail) return data.detail;
  if (error) return error;
  return "Something went wrong";
}

/** Unwrap GET/PATCH `patients/{id}` payloads into the flat profile object used by the UI. */
export function normalizePatientDetailResponse(response) {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return response ?? {};
  }

  const nested = response.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const looksLikePatient =
      nested.patientId != null ||
      nested.memberId != null ||
      nested.firstName != null ||
      nested.diagnosedConditions != null ||
      nested.allergies != null ||
      nested.userPersonalInterests != null;
    if (looksLikePatient) return nested;
  }

  return response;
}

function flattenMedicationScheduleEntries(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return [];

  const first = entries[0];
  if (first && Array.isArray(first.medications)) {
    return entries.flatMap((entry) => entry.medications ?? []);
  }

  return entries;
}

/** Normalize patient-medications/summary payloads into a flat medication array. */
export function normalizeMedicationScheduleList(response) {
  if (!response) return [];
  if (Array.isArray(response)) return flattenMedicationScheduleEntries(response);
  if (typeof response !== "object") return [];

  if (Array.isArray(response.medications)) return response.medications;

  const nested = response.data;
  if (Array.isArray(nested)) return flattenMedicationScheduleEntries(nested);
  if (nested && typeof nested === "object") {
    if (Array.isArray(nested.medications)) return nested.medications;
    if (Array.isArray(nested.medicationSchedule)) {
      return flattenMedicationScheduleEntries(nested.medicationSchedule);
    }
  }

  if (Array.isArray(response.medicationSchedule)) {
    return flattenMedicationScheduleEntries(response.medicationSchedule);
  }

  return [];
}

function isUnauthorized(status, data) {
  let message = "";
  if (typeof data?.message === "string") {
    message = data.message.toLowerCase();
  } else if (typeof data === "string") {
    message = data.toLowerCase();
  }

  return (
    status === 401 ||
    status === 403 ||
    data?.message === "Unauthorized" ||
    message.includes("unauthorized") ||
    message.includes("token expired") ||
    message.includes("session expired") ||
    message.includes("invalid token")
  );
}

function handleUnauthorized(api) {
  clearSessionAndRedirectToLogin(api.dispatch, { replace: true });
}

function isAbortError(error, data, message) {
  const normalizedError = typeof error === "string" ? error.toLowerCase() : "";
  const normalizedData = typeof data === "string" ? data.toLowerCase() : "";
  const normalizedMessage =
    typeof message === "string" ? message.toLowerCase() : "";

  return (
    error === "AbortError" ||
    normalizedError.includes("aborterror") ||
    normalizedData.includes("aborterror") ||
    normalizedMessage.includes("aborterror") ||
    normalizedMessage.includes("signal is aborted")
  );
}

function shouldSuppressToast(message) {
  const normalizedMessage =
    typeof message === "string"
      ? message.trim().toLowerCase()
      : String(message);

  const otpAlreadyVerified =
    normalizedMessage.includes("otp already verified") &&
    (normalizedMessage.includes("reset your ") && containsCredentialToken(normalizedMessage));

  const accountExists =
    normalizedMessage.includes("account with this email already exists") ||
    normalizedMessage.includes(
      "account with this phone number already exists"
    ) ||
    normalizedMessage.includes(
      "account with this mobile already exists"
    ) ||
    normalizedMessage.includes("account already exists");

  return otpAlreadyVerified || accountExists;
}

function showErrorToast(message) {
  const now = Date.now();
  const normalizedMessage =
    typeof message === "string"
      ? message.trim().toLowerCase()
      : String(message);

  const toastKey = `apiSlice:error:${normalizedMessage}`;
  const isDuplicate =
    normalizedMessage === lastToastMessage && now - lastToastAt < 3000;

  if (isDuplicate) return;

  toast.error(message, { toastId: toastKey });
  lastToastMessage = normalizedMessage;
  lastToastAt = now;
}

function createErrorResponse(status, message) {
  return { error: { status, data: message } };
}

async function executeBaseQuery(args, api, extraOptions) {
  return dynamicBaseQuery(resolveRequestArgs(args), api, extraOptions);
}

function buildAuthRefreshUrl() {
  const authBaseUrl =
    SERVICE_BASE_URLS.auth || SERVICE_BASE_URLS.common || "http://localhost:8090/api/";
  const cleanBase = authBaseUrl.endsWith("/") ? authBaseUrl : `${authBaseUrl}/`;
  return cleanBase.endsWith("/auth/") ? `${cleanBase}refresh` : `${cleanBase}auth/refresh`;
}

function readRefreshTokensFromResponse(refreshData) {
  const newToken =
    refreshData?.token?.accessToken ||
    refreshData?.accessToken ||
    refreshData?.token ||
    refreshData?.data?.token;
  const newRefreshToken =
    refreshData?.token?.refreshToken || refreshData?.refreshToken;
  return { newToken, newRefreshToken };
}

function persistRefreshedTokens(api, newToken, newRefreshToken) {
  api.dispatch(loginSuccess(newToken));
  if (newRefreshToken) {
    localStorage.setItem("refreshToken", newRefreshToken);
    sessionStorage.setItem("refreshToken", newRefreshToken);
  }
}

function getStoredRefreshToken() {
  return localStorage.getItem("refreshToken") || sessionStorage.getItem("refreshToken");
}

function shouldAttemptTokenRefresh(api, result) {
  if (!result?.error) return false;

  const { status, data } = result.error;
  if (!isUnauthorized(status, data)) return false;
  if (api.endpoint === "login" || api.endpoint === "refresh") return false;

  return Boolean(getStoredRefreshToken());
}

async function retryAfterTokenRefresh(args, api, extraOptions) {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const refreshResponse = await fetch(buildAuthRefreshUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!refreshResponse.ok) return null;

    const refreshData = await refreshResponse.json();
    const { newToken, newRefreshToken } = readRefreshTokensFromResponse(refreshData);
    if (!newToken) return null;

    persistRefreshedTokens(api, newToken, newRefreshToken);
    return executeBaseQuery(args, api, extraOptions);
  } catch (refreshError) {
    console.error("Token refresh failed:", refreshError);
    return null;
  }
}

async function apiBaseQuery(args, api, extraOptions) {
  let result = await executeBaseQuery(args, api, extraOptions);

  if (shouldAttemptTokenRefresh(api, result)) {
    const retried = await retryAfterTokenRefresh(args, api, extraOptions);
    if (retried) result = retried;
  }

  if (result?.error) {
    return processErrorResult(result, api, api?.endpoint);
  }

  return { data: result.data };
}

function processErrorResult(result, api, endpoint) {
  const { status, data, error } = result.error;
  const message = extractErrorMessage(result.error);

  if (isUnauthorized(status, data)) {
    handleUnauthorized(api);
    return createErrorResponse(status, message);
  }

  if (isAbortError(error, data, message)) {
    return createErrorResponse(status, message);
  }

  const locallyHandled = LOCALLY_HANDLED_ERROR_ENDPOINTS.has(endpoint);
  if (!locallyHandled && !shouldSuppressToast(message)) {
    showErrorToast(message);
  }

  return createErrorResponse(status, message);
}

// ─── Token refresh helper ─────────────────────────────────────────────────────
async function attemptTokenRefresh(api) {
  const refreshToken =
    localStorage.getItem("refreshToken") ||
    sessionStorage.getItem("refreshToken");

  if (!refreshToken) return null;

  try {
    const authBaseUrl =
      SERVICE_BASE_URLS.auth ||
      SERVICE_BASE_URLS.common ||
      "http://localhost:8090/api/";
    const cleanBase = authBaseUrl.endsWith("/")
      ? authBaseUrl
      : `${authBaseUrl}/`;
    const refreshUrl = cleanBase.endsWith("/auth/")
      ? `${cleanBase}refresh`
      : `${cleanBase}auth/refresh`;

    const refreshResponse = await fetch(refreshUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!refreshResponse.ok) return null;

    const refreshData = await refreshResponse.json();
    const newToken =
      refreshData?.token?.accessToken ||
      refreshData?.accessToken ||
      refreshData?.token ||
      refreshData?.data?.token;
    const newRefreshToken =
      refreshData?.token?.refreshToken || refreshData?.refreshToken;

    if (!newToken) return null;

    // ✅ Sync BOTH Redux state AND localStorage so prepareHeaders always
    //    finds a fresh token regardless of which path it reads from.
    api.dispatch(loginSuccess(newToken));
    localStorage.setItem("token", newToken);
    sessionStorage.setItem("token", newToken);

    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
      sessionStorage.setItem("refreshToken", newRefreshToken);
    }

    return newToken;
  } catch (err) {
    console.error("Token refresh failed:", err);
    return null;
  }
}
// ──────────────────────────────────────────────────────────────────────────────

async function retryAfterUnauthorized(args, api, extraOptions, result) {
  const { status, data } = result.error;
  const isAuthEndpoint =
    api.endpoint === "login" || api.endpoint === "refresh";

  if (!isUnauthorized(status, data) || isAuthEndpoint) {
    return result;
  }

  if (isRefreshing) {
    const newToken = await new Promise((resolve) => {
      subscribeTokenRefresh(resolve);
    });

    if (newToken) {
      return executeBaseQuery(args, api, extraOptions);
    }
    return result;
  }

  isRefreshing = true;
  const newToken = await attemptTokenRefresh(api);
  isRefreshing = false;

  if (newToken) {
    onTokenRefreshed(newToken);
    return executeBaseQuery(args, api, extraOptions);
  }

  onTokenRefreshFailed();
  handleUnauthorized(api);
  return createErrorResponse(status, extractErrorMessage(result.error));
}

export const apiSlice = createApi({
  reducerPath: "api",
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: true,

  baseQuery: async (args, api, extraOptions) => {
    let result = await executeBaseQuery(args, api, extraOptions);

    if (result?.error) {
      result = await retryAfterUnauthorized(args, api, extraOptions, result);
    }

    if (result?.error) {
      return processErrorResult(result, api, api?.endpoint);
    }

    return { data: result.data };
  },

  tagTypes: [
    "Items",
    "Patients",
    "Appointments",
    "FamilyMembers",
    "Allergies",
    "medication",
    "chat",
    "dashboardData",
    "chat-summary",
    "ProfileSwitch",
    "Vitals",
    "MedCloudFiles",
    "HealthInterestCategories",
  ],

  endpoints: (builder) => ({
    // GET Requests
    getallergiesChronicDeasese: builder.query({
      query: (searchText) => ({
        url: `icd10/search/allergy?query=${searchText}`,
        service: "common",
      }),
      providesTags: ["Allergies"],
    }),
    getallDiagnosisDeasese: builder.query({
      query: (searchText) => ({
        url: `icd10/search/diagnosis?query=${searchText}`,
        service: "common",
      }),
      providesTags: ["Diagnosis"],
    }),
    getallHerediateryDeasese: builder.query({
      query: (searchText) => ({
        url: `icd10/search/hereditary-disease?query=${searchText}`,
        service: "common",
      }),
      providesTags: ["herediatery"],
    }),



    getmedicationSupplement: builder.query({
      query: (searchText) => ({
        url: `medications/search?keyword=${searchText}`,
        service: "common",
      }),
      providesTags: ["medication"],
    }),
    getSupplement: builder.query({
      query: (searchText) => ({
        url: `medications/supplements/dropdown?search=${searchText}`,
        service: "common",
      }),
      providesTags: ["supplements"],
    }),

    getPatients: builder.query({
      query: () => ({ url: "patients", service: "common" }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: "Patients", id })),
            { type: "Patients", id: "LIST" },
          ]
          : [{ type: "Patients", id: "LIST" }],
    }),
    getAppointments: builder.query({
      query: (patientId) => ({ url: `appointments/getAppointment/${patientId}`, service: "common" }),
      providesTags: ["Appointments", "ProfileSwitch"],
    }),

    // POST Requests
    login: builder.mutation({
      query: (data) => ({
        url: "login",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["login"],
    }),
    /** POST auth base + login/google — body: { idToken } (Google OIDC JWT). Adjust url if your API differs. */
    loginWithGoogle: builder.mutation({
      query: (data) => ({
        url: "google",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["login", "Gamification"],
    }),
    register: builder.mutation({
      query: (data) => ({
        url: "signup",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["signup"],
    }),
    [FORGOT_CREDENTIAL_MUTATION_KEY]: builder.mutation({
      query: (data) => ({
        url: FORGOT_CREDENTIAL_API_PATH,
        service: "auth",
        method: "POST",
        body: data,
      }),
    }),

    checkExistOrNot: builder.mutation({
      query: (data) => ({
        url: "users/name-by-phone",
        service: "base",
        method: "POST",
        body: data,
      }),
    }),

    chatbot: builder.mutation({
      query: (data) => ({
        url: "chat",
        service: "dsApis",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),
    call: builder.query({
      query: (data) => ({
        url: `livekit/token?room=${data.room}&identity=${data.identity}`,
        service: "dsApis",
        method: "GET",

      }),
      providesTags: ["token"],
    }),

    chatbotWithoutAuth: builder.mutation({
      query: (data) => ({
        url: "chat/auth",
        service: "dsApis",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),

    dietChat: builder.mutation({
      query: (data) => ({
        url: "diet/chat",
        service: "dsApis",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),

    sendotp: builder.mutation({
      query: (data) => ({
        url: "otp/generate",
        service: "common",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),

    /** Auth service (REACT_APP_AUTH_SERVICE_URL): POST generate-email-otp — body `{ email }` only. */
    generateEmailOtp: builder.mutation({
      query: ({ email }) => ({
        url: "generate-email-otp",
        service: "auth",
        method: "POST",
        body: { email: String(email ?? "").trim() },
      }),
    }),

    verifyotp: builder.mutation({
      query: (data) => ({
        url: "otp/verify",
        service: "common",
        method: "POST",
        body: data,
      }),
    }),

    verifyEmailOtp: builder.mutation({
      query: (data) => ({
        url: "email/verify-otp",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Gamification"],
    }),

    sendPhoneOtp: builder.mutation({
      query: (data) => ({
        url: "mobile/send-otp",
        service: "auth",
        method: "POST",
        body: data,
      }),
    }),
    verifyPhoneOtpForRegister: builder.mutation({
      query: (data) => ({
        url: "otp/verify/mobile",
        service: "common",
        method: "POST",
        body: data,
      }),
    }),

    verifyPhoneOtp: builder.mutation({
      query: (data) => ({
        url: "mobile/verify-otp",
        service: "auth",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Gamification"],
    }),

    extractReport: builder.mutation({
      query: (data) => ({
        url: "extract/document",
        service: "dsApis2",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
    }),


    extractReportauth: builder.mutation({
      query: (data) => ({
        url: "extract/document/auth",
        service: "dsApis2",
        method: "POST",
        body: data,
      }),
      providesTags: ["chat"],
      transformErrorResponse: (response) => {
        return response.data; // Pass error data forward
      },
    }),



    addFamilyMember: builder.mutation({
      query: (data) => ({
        url: "user-relationships/addFamilyMember",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["FamilyMembers", "Gamification"],
    }),
    /** PUT user-relationships/{relatedUserId} — body: relationship fields from list row */
    updateFamilyMember: builder.mutation({
      query: ({ relatedUserId, ...body }) => ({
        url: `user-relationships/${relatedUserId}`,
        service: "common",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["FamilyMembers"],
    }),
    createPatient: builder.mutation({
      query: (data) => ({
        url: "patients",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Patients", id: "LIST" }, "Gamification"],
    }),

    updatePatient: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `patients/${id}`,
        service: "common",
        method: "POST",
        body: data,
      }),
      transformResponse: (response) => normalizePatientDetailResponse(response),
      invalidatesTags: (result, error, { id }) => [
        { type: "Patients", id },
        "ProfileSwitch",
        "dashboardData",
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedPatient } = await queryFulfilled;
          const patient = normalizePatientDetailResponse(updatedPatient);
          if (!patient || typeof patient !== "object") return;

          dispatch(
            apiSlice.util.updateQueryData("getProfileDetail", id, (draft) => {
              Object.assign(draft, patient);
            })
          );

          dispatch(
            apiSlice.util.updateQueryData("getPatients", undefined, (draft) => {
              const index = draft.findIndex(
                (p) => String(p.id) === String(id) || String(p.patientId) === String(id)
              );
              if (index !== -1) {
                draft[index] = { ...draft[index], ...patient };
              }
            })
          );
        } catch { }
      },
    }),
    createAppointment: builder.mutation({
      query: (data) => ({
        url: "appointments/create",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appointments"],
    }),



    getdashboardData: builder.query({
      query: (patientId) => ({
        url: `dashboard/getDashboard?patientId=${patientId}`,
        service: "common",
      }),
      serializeQueryArgs: ({ queryArgs }) => String(queryArgs ?? ""),
      transformResponse: (response) => {
        const videos = extractDashboardVideosPayload(response);
        if (!response || typeof response !== "object" || !Array.isArray(videos)) {
          return response;
        }

        const root = { ...response };
        const nested = root.data;
        if (nested && typeof nested === "object" && !Array.isArray(nested)) {
          root.data = { ...nested, videos };
        } else if (!root.data) {
          root.data = { videos };
        }
        return root;
      },
      providesTags: ["dashboardData", "ProfileSwitch"],
    }),

    getChatSummaryList: builder.query({
      query: (userId) => ({ url: `summary/user/${userId}`, service: "dsApis" }),
      providesTags: ["chat-summary", "ProfileSwitch"],
    }),

    getChatSummaryLogs: builder.query({
      query: (userId) => ({ url: `history/${userId}`, service: "dsApis" }),
      providesTags: ["chat-summary", "ProfileSwitch"],
    }),


    getMedicationSchedule: builder.query({
      query: ({ patientId, status }) => ({
        url: `patient-medications/summary/${patientId}?status=${status !== undefined && status !== null ? status : true}`,
        service: "common"
      }),
      transformResponse: (response) => normalizeMedicationScheduleList(response),
      providesTags: ["ProfileSwitch"],
    }),

    deactivateMedication: builder.mutation({
      query: ({ patientId, patientMedicationId }) => ({
        url: `patient-medications/${patientId}/deactivate/${patientMedicationId}`,
        service: "common",
        method: "POST",
      }),
      invalidatesTags: ["ProfileSwitch"],
    }),


    createMedications: builder.mutation({
      query: (data) => ({
        url: "drugIntake/schedule",
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appointments", "ProfileSwitch", "medication"],
    }),

    gemifications: builder.query({
      query: (data) => ({
        url: `gamification/${data}`,
        service: "base",
        method: "GET",

      }),
      providesTags: ["Gamification"],

    }),

    getHealthCareSummary: builder.query({
      query: (patientId) => ({
        url: `health-summary/user/${patientId}`,

        service: "dsApis2",
        method: "GET",
      }),
      providesTags: ["ProfileSwitch"],
    }),
    getPatientList: builder.query({
      query: (primaryUserId) => ({
        url: `user-relationships/related-users?primaryUserId=${primaryUserId}`,

        service: "common",
        method: "GET",
      }),  
      providesTags: ["FamilyMembers"],
    }),


    getProfileDetail: builder.query({
      query: (userID) => ({
        url: `patients/${userID}`,
        service: "common",
        method: "GET",
      }),
      transformResponse: (response) => normalizePatientDetailResponse(response),
      providesTags: (result, error, id) => [
        { type: "Patients", id },
        "ProfileSwitch",
      ],
    }),

    // Inside apiSlice.ts
    getVitalsReport: builder.query({
      query: ({ patientId, vitalType, startDate, endDate, page, size }) => {
        const parts = [];
        if (vitalType) parts.push(`vitalType=${vitalType}`);
        if (startDate) parts.push(`startDate=${startDate}`);
        if (endDate) parts.push(`endDate=${endDate}`);
        if (page !== undefined && page !== null) parts.push(`page=${page}`);
        if (size !== undefined && size !== null) parts.push(`size=${size}`);
        const url = parts.length > 0 ? `vitals/patient/${patientId}?${parts.join("&")}` : `vitals/patient/${patientId}`;
        return {
          url,
          service: "apiV1",
          method: "GET",
        };
      },
      providesTags: (result, error, { vitalType }) => [
        { type: "Vitals", id: vitalType || 'ALL' },
      ],
    }),
    getHealthInterestCategories: builder.query({
      query: () => ({
        url: "health-interests/categories",
        service: "apiV1",
        method: "GET",
      }),
      transformResponse: (response) =>
        mapHealthInterestCategoriesResponse(response),
      providesTags: ["HealthInterestCategories"],
    }),

    saveUserInterests: builder.mutation({
      query: (body) => ({
        url: "user-interests",
        service: "common",
        method: "POST",
        body,
      }),
      invalidatesTags: ["dashboardData"],
    }),

    acceptTermsAndConditions: builder.mutation({
      query: (body) => ({
        url: "consents",
        service: "apiV1",
        method: "POST",
        body,
      }),
      invalidatesTags: ["updatedata", "dashboardData"],
    }),
    restoreUserAccount: builder.mutation({
      query: (body) => ({
        url: "users/user/edit",
        service: "common",
        method: "POST",
        body,
      }),
      invalidatesTags: ["updatedata", "dashboardData"],
    }),
    getPatientLabReports: builder.query({
      query: ({ patientId, documentCategory, page = 0, size = 10 }) => ({
        url: `lab-reports/patient/${patientId}?documentCategory=${encodeURIComponent(
          documentCategory
        )}&page=${page}&size=${size}`,
        service: "common",
        method: "GET",
      }),
      providesTags: ["ProfileSwitch"],
    }),

    medCloudUpload: builder.mutation({
      query: (payload) => ({
        url: "medcloud/upload",
        service: "dsApis4",
        method: "POST",
        body: buildMedCloudUploadFormData(payload),
      }),
      invalidatesTags: (result, error, payload) => [
        {
          type: "MedCloudFiles",
          id: `${payload?.user_id ?? ""}-${payload?.patient_id ?? ""}`,
        },
      ],
    }),

    medCloudDeleteFile: builder.mutation({
      query: ({ fileId, userId, patientId }) => {
        const params = new URLSearchParams();
        params.set("user_id", String(userId));
        const resolvedPatient =
          patientId == null || patientId === "" ? "" : String(patientId).trim();
        if (resolvedPatient) {
          params.set("patient_id", resolvedPatient);
        }
        return {
          url: `medcloud/files/${encodeURIComponent(String(fileId))}?${params.toString()}`,
          service: "dsApis4",
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, { userId, patientId }) => [
        {
          type: "MedCloudFiles",
          id: `${userId ?? ""}-${patientId ?? ""}`,
        },
      ],
    }),

    medCloudDownloadFile: builder.query({
      query: ({ fileId, userId, patientId }) => {
        const params = new URLSearchParams();
        params.set("user_id", String(userId));
        const resolvedPatient =
          patientId == null || patientId === "" ? "" : String(patientId).trim();
        if (resolvedPatient) {
          params.set("patient_id", resolvedPatient);
        }
        return {
          url: `medcloud/files/${encodeURIComponent(String(fileId))}?${params.toString()}`,
          service: "dsApis4",
          method: "GET",
          responseHandler: async (response) => {
            if (!response.ok) {
              let detail = `Request failed (${response.status})`;
              try {
                const errJson = await response.json();
                if (typeof errJson?.detail === "string") {
                  detail = errJson.detail;
                }
              } catch {
                // ignore non-json errors
              }
              throw new Error(detail);
            }

            const blob = await response.blob();
            return {
              blob,
              contentType: response.headers.get("content-type") || blob.type || "",
            };
          },
        };
      },
    }),

    medCloudFilesList: builder.query({
      query: ({ userId, patientId }) => ({
        url: `medcloud/files?user_id=${userId}&patient_id=${patientId}`,
        service: "dsApis4",
        method: "GET",

      }),
      providesTags: (result, error, { userId, patientId }) => [
        {
          type: "MedCloudFiles",
          id: `${userId ?? ""}-${patientId ?? ""}`,
        },
      ],
    }),

    addEvents: builder.mutation({
      query: (data) => ({
        url: `calendar-events/${data.patientId}`,
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Events"],
    }),

    markTaken: builder.mutation({
      query: (data) => ({
        url: `drugIntake/mark-taken?patientId=${data.patientId}&drugIntakeProgressId=${data.drugIntakeProgressId}`,
        service: "common",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appointments", "ProfileSwitch", "medication", "Gamification"],
    }),

    changePassword: builder.mutation({
      query: (data) => ({
        url: RESET_CREDENTIAL_API_PATH,
        service: "common",
        method: "POST",
        body: data,
      }),

    }),

    requestAccountDeletion: builder.mutation({
      query: (data) => ({
        url: `account`,
        service: "common",
        method: "DELETE",
        body: data,
      }),
    }),

    cancelAccountDeletion: builder.mutation({
      query: (data) => ({
        url: `auth/account/cancel-delete`,
        service: "common",
        method: "POST",
        body: data ?? {},
      }),
    }),

    getAccountDeletionStatus: builder.query({
      query: (userId) => ({
        url: `auth/account/deletion-status/${userId}`,
        service: "common",
        method: "GET",
      }),
    }),

    ListingEvents: builder.query({
      query: (data) => {
        const typeParam = data.appointmentType && data.appointmentType !== "undefined" ? `&appointmentType=${data.appointmentType}` : "";
        return {
          url: `calendar-events/${data.patientId}?startDate=${data.startDate}&endDate=${data.endDate}${typeParam}`,
          service: "common",
          method: "GET",
        };
      },
      providesTags: ["Events"],
    }),

    medicationSingleView: builder.query({
      query: (patientId) => ({
        url: `patient-medications/summary/${patientId}`,
        method: "GET",
        service: "common",
      }),
      providesTags: ["Events"],
    }),

    medicationDetailView: builder.query({
      query: (patientMedId) => ({
        url: `patient-medications/drug-intake/${patientMedId}`,
        method: "GET",
        service: "common",
      }),
      providesTags: ["Events"],
    }),

    medicationDetailViewAlerts: builder.mutation({
      query: (payload) => ({
        url: "drug/medicine-profile",
        method: "POST",
        service: "dsApis",
        body: payload,
      }),
    }),

    logMedicationObservation: builder.mutation({
      query: ({ patientMedId, ...body }) => ({
        url: `patient-medications/drug-intake/${patientMedId}`,
        service: "common",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Events", "ProfileSwitch"],
    }),

    notifications: builder.query({
      query: ({ patientID, page = 0, size = 10 }) => ({
        url: `notifications/patient/${patientID}?page=${page}&size=${size}`,
        method: "GET",
        service: "common",
      }),
      providesTags: ["Notifications"],
    }),

    markAllNotificationsRead: builder.mutation({
      query: (patientID) => ({
        url: `notifications/patient/${patientID}/mark-all-read`,
        method: "POST",
        service: "common",
      }),
      async onQueryStarted(patientID, { dispatch, queryFulfilled, getState }) {
        const patches = [];

        for (const entry of Object.values(getState()?.api?.queries ?? {})) {
          if (entry?.endpointName !== "notifications") continue;
          if (entry?.originalArgs?.patientID !== patientID) continue;

          patches.push(
            dispatch(
              apiSlice.util.updateQueryData(
                "notifications",
                entry.originalArgs,
                (draft) => {
                  if (!draft) return;

                  if (Array.isArray(draft)) {
                    draft.forEach((item) => {
                      item.read = true;
                    });
                    return;
                  }

                  if (draft.data && typeof draft.data === "object") {
                    if (typeof draft.data.unreadCount === "number") {
                      draft.data.unreadCount = 0;
                    }
                    const items =
                      draft.data.items ||
                      draft.data.notifications?.items ||
                      [];
                    if (Array.isArray(items)) {
                      items.forEach((item) => {
                        item.read = true;
                      });
                    }
                  }

                  if (typeof draft.unreadCount === "number") {
                    draft.unreadCount = 0;
                  }

                  const rootItems = draft.items || draft.notifications;
                  if (Array.isArray(rootItems)) {
                    rootItems.forEach((item) => {
                      item.read = true;
                    });
                  }
                }
              )
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: ["Notifications"],
    }),
  }),





});


export const {
  useGetVitalsReportQuery,
  useGetProfileDetailQuery,
  useGetPatientListQuery,
  useGetHealthCareSummaryQuery,
  useCreateMedicationsMutation,
  useGetMedicationScheduleQuery,
  useGetChatSummaryLogsQuery,
  useGetallergiesChronicDeaseseQuery,
  useGetmedicationSupplementQuery,
  useLoginMutation,
  useLoginWithGoogleMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useChatbotMutation,
  useChatbotWithoutAuthMutation,
  useDietChatMutation,
  useGetChatSummaryListQuery,
  useSendotpMutation,
  useGenerateEmailOtpMutation,
  useVerifyotpMutation,
  useVerifyEmailOtpMutation,
  useSendPhoneOtpMutation,
  useVerifyPhoneOtpMutation,
  useExtractReportMutation,
  useAddFamilyMemberMutation,
  useUpdateFamilyMemberMutation,
  useGetPatientsQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useGetAppointmentsQuery,
  useCreateAppointmentMutation,
  useExtractReportauthMutation,
  useGetdashboardDataQuery,
  useGetPatientLabReportsQuery,
  useVerifyPhoneOtpForRegisterMutation,
  useCheckExistOrNotMutation,
  useGemificationsQuery,
  useAddEventsMutation,
  useMarkTakenMutation,
  useListingEventsQuery,
  useMedicationSingleViewQuery,
  useGetallHerediateryDeaseseQuery,
  useGetallDiagnosisDeaseseQuery,
  useGetSupplementQuery,
  useLazyCallQuery,
  useNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMedCloudUploadMutation,
  useMedCloudDeleteFileMutation,
  useMedCloudDownloadFileQuery,
  useLazyMedCloudDownloadFileQuery,
  useMedCloudFilesListQuery,
  useChangePasswordMutation,
  useRequestAccountDeletionMutation,
  useCancelAccountDeletionMutation,
  useRestoreUserAccountMutation,
  useGetAccountDeletionStatusQuery,
  useLazyGetAccountDeletionStatusQuery,
  useGetHealthInterestCategoriesQuery,
  useSaveUserInterestsMutation,
  useAcceptTermsAndConditionsMutation,
  useMedicationDetailViewQuery,
  useMedicationDetailViewAlertsMutation,
  useLogMedicationObservationMutation,
  useDeactivateMedicationMutation
} = apiSlice;
